/**
 * The task half of the dispatch form, without the form: its category
 * vocabulary, its field values, and the two directions between those
 * values and a v1 `TaskRequest`.
 *
 * Moved out of `TaskDispatchForm.tsx` unchanged so a second editor —
 * changing a SAVED schedule's task — speaks exactly the dialect the
 * create form writes, instead of a copy that drifts from it. Everything
 * from `toV1Category` to `buildPhases` is that form's code, verbatim.
 *
 * The new part is the way back (`formFromTaskRequest`) and the merge
 * (`taskRequestFromForm`). Rebuilding a stored task from form values
 * would lose whatever the form cannot hold: the robot pin (the form has
 * no robot field), phase params it never writes, phase ids, an old 1-5
 * priority the form now reads as Normal. So an edit is
 * applied as a DIFF — the fields the operator changed, written over the
 * original — and a task the form cannot express at all is refused with
 * a reason rather than flattened into the nearest one it can.
 */
import type { TaskCategory, TaskPhase, TaskPhaseKind, TaskRequest } from '../../api/v1';
import { CLEAN_MODE_LABEL, DEFAULT_CLEAN_MODE, toCleanMode, type CleanMode } from './clean-modes';
import { PRIORITY_NORMAL, priorityLevel } from '../../api/task-priority';

/**
 * The bounds on the dustbag form's optional wait, in whole minutes.
 *
 * 240 is the worker's own ceiling (`_DUSTBAG_MAX_TIMEOUT_S`, four
 * hours). Above it the worker clamps, so a larger number here would
 * tell staff a wait the robot does not keep. 5 is the floor: walking
 * to the bin station, changing the bag and pressing Confirm takes
 * longer than that, so a shorter wait sends the robot away before
 * anyone could have arrived.
 */
export const DUSTBAG_WAIT_MIN_MINUTES = 5;
export const DUSTBAG_WAIT_MAX_MINUTES = 240;

/**
 * The wait field read as whole minutes: `null` when empty (send
 * nothing, the site default applies), the number when it is a whole
 * number inside the bounds, `'invalid'` otherwise.
 *
 * Invalid is REFUSED at the form rather than rounded or clamped into
 * range. The worker refuses a malformed `timeout_s` outright, and the
 * whole point of the field is that somebody chose a number; quietly
 * sending a different one is the failure it exists to avoid.
 */
export function parseDustbagWaitMinutes(raw: string | undefined): number | null | 'invalid' {
  const text = raw?.trim() ?? '';
  if (!text) return null;
  if (!/^\d+$/.test(text)) return 'invalid';
  const minutes = Number(text);
  if (minutes < DUSTBAG_WAIT_MIN_MINUTES || minutes > DUSTBAG_WAIT_MAX_MINUTES) return 'invalid';
  return minutes;
}

/**
 * The form's internal category vocabulary (v1 speaks `station_docking`
 * where this says `station` — see `toV1Category`).
 *
 * `delivery` used to be here, with a pickup/dropoff branch. It was dead
 * code at every site: the worker has no delivery description builder,
 * so the category never reaches any site's task catalog and the branch
 * could never be offered. The v1 contract and the backend KEEP the
 * category — a future delivery builder resurrects it server-first via
 * the catalog (add the worker builder and the option reappears), at
 * which point this union gets it back along with its form branch.
 *
 * Exported so a parent can type a `prefillCategory` prop.
 */
export type Category = 'patrol' | 'station' | 'clean' | 'dustbag' | 'charge';

const FORM_CATEGORIES: readonly Category[] = ['patrol', 'station', 'clean', 'dustbag', 'charge'];

export interface CategoryFormValues {
  destination?: string;
  waypoints?: string;
  zone?: string;
  /** The DustBagStation a `dustbag` task drives to. Its OWN field
   *  rather than a reuse of `destination`: that one is a free-text
   *  place input validated against the whole waypoint registry, and a
   *  dustbag station must come from the catalog's short declared list
   *  or not at all. Sharing the field would let a station survive a
   *  category switch and be dispatched as the wrong thing. */
  dustbagStation?: string;
  /** The dustbag's optional wait, as typed, in minutes. Kept as the raw
   *  text so a bad entry can be named back to the operator rather than
   *  silently coerced (`parseDustbagWaitMinutes`). Empty is the normal
   *  case and sends nothing: the site's default applies. */
  dustbagWaitMinutes?: string;
  /** The charger place a `charge` task drives to. Its OWN field for
   *  the same reason `dustbagStation` is one: chargers come from the
   *  catalog's short site-declared list (`NEXSOSS_CHARGER_PLACES` ∩
   *  live nav graph) or not at all, and sharing `destination` would
   *  let a value survive a category switch and be dispatched as the
   *  wrong thing. */
  chargePlace?: string;
  fleet?: string;
  /** 0 Normal, 1 High (2026-10-31): always a level, never the old 1-5. */
  priority?: number;
  notes?: string;
  startTime?: string;
}

export const DEFAULT_CATEGORY_FORM_VALUES: CategoryFormValues = {
  destination: '',
  waypoints: '',
  zone: '',
  dustbagStation: '',
  dustbagWaitMinutes: '',
  chargePlace: '',
  notes: '',
  priority: PRIORITY_NORMAL,
  startTime: '',
};

/**
 * v1 `TaskCategory` doesn't have a bare "station" value — the form's
 * category selector predates the v1 contract and uses upstream RMF's
 * shorter naming; map to the schema's actual enum value.
 */
export function toV1Category(category: Category): TaskCategory {
  return category === 'station' ? 'station_docking' : category;
}

/**
 * The inverse, for reading the task catalog.
 *
 * The catalog is a v1 contract object and speaks v1 `TaskCategory`,
 * because that is the vocabulary the worker's builder table is keyed by
 * and the site descriptors declare — so the three can be compared
 * directly. This form predates that and branches internally on the
 * shorter UI names, so the boundary is translated here rather than by
 * renaming a union that `buildPhases`, `categoryRequiredComplete` and
 * every per-category field already switch on.
 */
export function fromV1Category(category: string): Category {
  return category === 'station_docking' ? 'station' : (category as Category);
}

/**
 * The picker's value for one clean mode. Vacuum keeps the bare `clean`
 * the picker has always used for the clean, so anything that selects
 * "clean" (a test, an older bookmark of the flow) still gets the one
 * mode every site has; the others are `clean:<mode>`.
 */
export function cleanPickerValue(mode: CleanMode): string {
  return mode === DEFAULT_CLEAN_MODE ? 'clean' : `clean:${mode}`;
}

export function parsePickerValue(value: string): { category: Category; mode?: CleanMode } {
  if (value === 'clean') return { category: 'clean', mode: DEFAULT_CLEAN_MODE };
  if (value.startsWith('clean:')) {
    const mode = value.slice('clean:'.length);
    return { category: 'clean', mode: toCleanMode(mode) ?? DEFAULT_CLEAN_MODE };
  }
  return { category: value as Category };
}

let phaseCounter = 0;
function nextPhaseId(): string {
  phaseCounter += 1;
  return `phase_${Date.now().toString(36)}_${phaseCounter}`;
}

function goToPlacePhase(place: string): TaskPhase {
  return {
    id: nextPhaseId(),
    kind: 'go_to_place' as TaskPhaseKind,
    description: place,
    params: { place },
    skipped: false,
  };
}

/**
 * Builds v1 `TaskPhase[]` from the form's category-specific fields.
 * This is a best-effort construction against the CURRENT v1 contract
 * (`kind: 'go_to_place'`/`'perform_action'` + a free-form `params`
 * bag) — the worker does not yet translate these into upstream
 * RMF's own per-category dispatch JSON (that translation, and the
 * exact shape RMF expects per category, is separately-scoped,
 * pending work), so a dispatched task is recorded and accepted at
 * the command-lifecycle layer but does not yet drive the real
 * simulation.
 */
export function buildPhases(
  category: Category,
  v: CategoryFormValues,
  cleanMode: CleanMode = DEFAULT_CLEAN_MODE,
): TaskPhase[] {
  if (category === 'station' && v.destination) {
    return [goToPlacePhase(v.destination)];
  }
  if (category === 'patrol' && v.waypoints) {
    const places = v.waypoints
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return places.map((p) => goToPlacePhase(p));
  }
  if (category === 'dustbag' && v.dustbagStation) {
    // ONE `go_to_place` and nothing else. The worker's
    // `_dustbag_description` reads the station out of exactly this
    // phase (unlike the clean, whose zone rides on a `perform_action`)
    // and REFUSES anything but a single place -- two would mean a
    // patrol was sent under a dustbag label. The perform_action that
    // actually waits for the ack is built worker-side, along with the
    // site's default wait.
    const phase = goToPlacePhase(v.dustbagStation);
    const waitMinutes = parseDustbagWaitMinutes(v.dustbagWaitMinutes);
    // Only a filled-in wait rides along, as `timeout_s` on this same
    // phase (the worker scans every phase's params for it). Empty sends
    // nothing so the SITE's default applies, not a copy of it taken
    // when the form loaded -- a favourite or a schedule saved today
    // keeps following the site if the site changes its mind.
    if (typeof waitMinutes !== 'number') return [phase];
    return [{ ...phase, params: { ...phase.params, timeout_s: waitMinutes * 60 } }];
  }
  if (category === 'charge' && v.chargePlace) {
    // ONE `go_to_place`, exactly like `station`: the worker dispatches
    // a charge as an RMF patrol of exactly one place (there is no
    // dispatchable ChargeBattery description — see the worker's charge
    // builder next to `_station_description`), and it refuses anything
    // but a single place. The category rides the same go-there shape;
    // what distinguishes it is that the place comes from the site's
    // declared charger list and the calendar/metrics file it as
    // `charge`. Note `TaskPhaseKind` does carry a `'charge'` kind —
    // that names RMF's OWN charging phases on the read side, not
    // something this form may request.
    return [goToPlacePhase(v.chargePlace)];
  }
  if (category === 'clean' && v.zone) {
    // `mode` always sent, vacuum included, so a stored schedule or
    // favourite says what it is rather than leaning on the default.
    // `action: 'clean'` is unchanged for all three: it is what the
    // worker's builder and the clean verifier key on.
    return [
      {
        id: nextPhaseId(),
        kind: 'perform_action' as TaskPhaseKind,
        description: `${CLEAN_MODE_LABEL[cleanMode]} ${v.zone}`,
        params: { zone: v.zone, action: 'clean', mode: cleanMode },
        skipped: false,
      },
    ];
  }
  return [];
}

/** The category/phases/priority/schema_ref fields a real dispatch, a
 *  saved favourite and a new schedule all share. */
export interface DispatchFields {
  category: TaskCategory;
  description: string;
  phases: TaskPhase[];
  priority: number;
  schema_ref: string;
}

/** The dispatch form's fields as those shared request fields; `null`
 *  when the category's required field(s) aren't filled in. The body of
 *  the form's `buildDispatchFields`, which says why. */
export function dispatchFieldsFromForm(
  category: Category,
  values: CategoryFormValues,
  cleanMode: CleanMode,
): DispatchFields | null {
  const phases = buildPhases(category, values, cleanMode);
  if (phases.length === 0) return null;
  const v1Category = toV1Category(category);
  return {
    category: v1Category,
    description: (values.notes && values.notes.trim()) || v1Category,
    phases,
    // A level, 0 or 1; the api-server refuses 1 from anyone who may not
    // set High (`task_priority.py`).
    priority: priorityLevel(values.priority),
    // Placeholder — no schema registry exists yet to resolve this
    // against; the dispatch UI is not actually schema-driven today
    // despite the v1 contract's field-level comment describing
    // that as the intent.
    schema_ref: `nexsoss:v1:task-dispatch/${v1Category}`,
  };
}

// ----------------------------------------------------------------------------
// The way back: a stored task as form values, and an edit as a merge
// ----------------------------------------------------------------------------

/** A task as the dispatch form holds it: the same three pieces of state
 *  the form keeps (`category`, `cleanMode`, `values`). */
export interface TaskRequestForm {
  readonly category: Category;
  /** Read for a `clean`; the default otherwise, as the form keeps it. */
  readonly cleanMode: CleanMode;
  readonly values: CategoryFormValues;
}

/** Why a stored task cannot be shown in the form. A clause that
 *  completes "its task can't be changed here because …". */
export interface UnsupportedTask {
  readonly unsupported: string;
}

export function isUnsupportedTask<T extends object>(
  value: T | UnsupportedTask,
): value is UnsupportedTask {
  return 'unsupported' in value;
}

function unsupportedTask(reason: string): UnsupportedTask {
  return { unsupported: reason };
}

/** The place a one-place phase list goes to: exactly one `go_to_place`
 *  naming a place, else null. */
function singlePlace(phases: readonly TaskPhase[]): string | null {
  if (phases.length !== 1) return null;
  const phase = phases[0]!;
  const place = phase.params?.place;
  return phase.kind === 'go_to_place' && typeof place === 'string' && place !== '' ? place : null;
}

/** The waypoint list as `buildPhases` reads it back: comma-split,
 *  trimmed, empties dropped. */
function waypointList(text: string | undefined): string[] {
  return (text ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * A stored task as the dispatch form's values, or why the form cannot
 * hold it.
 *
 * Expressible: exactly what `buildPhases` writes — one place for a
 * station, a charge or a routine check (plus the check's optional wait,
 * in whole minutes from 5 to 240); an ordered list of places for a
 * patrol; one clean `perform_action` with a zone and a mode this console
 * knows (none means vacuum, the old `wet_scrub` reads as scrub) — and
 * the fleet, priority and notes around them. Anything else (a delivery,
 * a custom task, a clean with extra phases, a wait of 90 s, a patrol
 * stop whose name has a comma in it) is `{ unsupported }`.
 *
 * What the form has no field for — a robot pin, phase params it never
 * writes, phase ids — does not stop a task being expressible: it is not
 * SHOWN, and `taskRequestFromForm` carries it through untouched. The
 * zone and places are NOT checked against the site's catalog here: an
 * editor shows a stored zone the catalog no longer offers as exactly
 * that, rather than this deciding for it.
 */
export function formFromTaskRequest(task: TaskRequest): TaskRequestForm | UnsupportedTask {
  const category = fromV1Category(task.category);
  if (!FORM_CATEGORIES.includes(category)) {
    return unsupportedTask(
      task.category === 'delivery'
        ? 'it is a delivery, which the form no longer offers'
        : `it is a ${task.category} task, which the form cannot build`,
    );
  }
  const phases = task.phases ?? [];
  const values: CategoryFormValues = {
    ...DEFAULT_CATEGORY_FORM_VALUES,
    // Its level (`priorityLevel`): an old 2-5 reads as Normal, as the
    // api-server sends it. The merge compares levels, so reading one does
    // not count as an edit.
    priority: priorityLevel(task.priority),
    // The description IS the notes, or the category when there were
    // none (`dispatchFieldsFromForm`).
    notes: task.description === toV1Category(category) ? '' : task.description,
    ...(task.fleet ? { fleet: task.fleet } : {}),
  };
  let cleanMode: CleanMode = DEFAULT_CLEAN_MODE;

  if (category === 'station' || category === 'charge') {
    const place = singlePlace(phases);
    if (place === null) return unsupportedTask('it does not go to exactly one place');
    if (category === 'station') values.destination = place;
    else values.chargePlace = place;
  } else if (category === 'dustbag') {
    const place = singlePlace(phases);
    if (place === null) return unsupportedTask('it does not go to exactly one place');
    values.dustbagStation = place;
    const timeoutS = phases[0]!.params?.timeout_s;
    if (timeoutS !== undefined && timeoutS !== null) {
      // Read back through the field's own parser, so "expressible" is
      // exactly "the form would send this again".
      const minutes = typeof timeoutS === 'number' ? timeoutS / 60 : NaN;
      if (!Number.isInteger(minutes) || parseDustbagWaitMinutes(String(minutes)) === 'invalid') {
        return unsupportedTask(
          `it waits ${String(timeoutS)} s for staff, and the form takes whole minutes from ${DUSTBAG_WAIT_MIN_MINUTES} to ${DUSTBAG_WAIT_MAX_MINUTES}`,
        );
      }
      values.dustbagWaitMinutes = String(minutes);
    }
  } else if (category === 'patrol') {
    if (phases.length === 0) return unsupportedTask('it goes nowhere');
    const places: string[] = [];
    for (const phase of phases) {
      const place = phase.params?.place;
      if (phase.kind !== 'go_to_place' || typeof place !== 'string' || place === '') {
        return unsupportedTask('it does more than visit places');
      }
      // The waypoint list is comma-separated and trimmed on the way
      // back in, so a name that carries either would be split or
      // re-spelled by the first save.
      if (place.includes(',') || place !== place.trim()) {
        return unsupportedTask(`a stop's name ("${place}") cannot be written in the waypoint list`);
      }
      places.push(place);
    }
    values.waypoints = places.join(', ');
  } else {
    const phase = phases.length === 1 ? phases[0]! : null;
    const zone = phase?.params?.zone;
    if (
      !phase ||
      phase.kind !== 'perform_action' ||
      phase.params?.action !== 'clean' ||
      typeof zone !== 'string' ||
      zone === ''
    ) {
      return unsupportedTask('it is not a single clean of one zone');
    }
    const rawMode = phase.params?.mode;
    const mode =
      rawMode === undefined || rawMode === null ? DEFAULT_CLEAN_MODE : toCleanMode(rawMode);
    if (!mode)
      return unsupportedTask(`its clean mode (${String(rawMode)}) is not one this console knows`);
    values.zone = zone;
    cleanMode = mode;
  }
  return { category, cleanMode, values };
}

/** `obj` without `key` — absent, not `undefined`, so the request reads
 *  as one that never named it ("no preference" is not `fleet: null`). */
function omitKey<T extends object>(obj: T, key: string): T {
  const copy = { ...obj } as Record<string, unknown>;
  delete copy[key];
  return copy as T;
}

function withPlace(phase: TaskPhase, place: string): TaskPhase {
  // The description follows the place, as `goToPlacePhase` writes it;
  // the id and every other param stay.
  return { ...phase, description: place, params: { ...phase.params, place } };
}

/**
 * The stored task with the form's edits applied — and NOTHING else
 * changed.
 *
 * Each field is compared with what `formFromTaskRequest` showed for the
 * same task, and only a field that differs is written, over the
 * original. So an untouched form returns `original` itself; a robot
 * pin, the stored priority, phase ids and params the form never writes all
 * survive an edit to the zone; and a changed zone or place patches the
 * existing phase rather than replacing it.
 *
 * Refused (`{ unsupported }`) when the original is one the form cannot
 * hold, when the category differs (Edit keeps a schedule's type — a
 * different type is a different schedule), or when an edited field
 * cannot be sent: an emptied required field, an unreadable wait.
 *
 * A changed FLEET drops the robot pin with it — a pinned robot belongs
 * to the pinned fleet. The schedule editor shows the pin read-only, so
 * this is for a caller that offers the fleet choice the create form
 * does.
 */
export function taskRequestFromForm(
  form: TaskRequestForm,
  original: TaskRequest,
): TaskRequest | UnsupportedTask {
  const base = formFromTaskRequest(original);
  if (isUnsupportedTask(base)) return base;
  if (form.category !== base.category) {
    return unsupportedTask("its type can't be changed: make a new schedule instead");
  }
  const v = form.values;
  const b = base.values;
  const changed = (a: string | undefined, c: string | undefined) => (a ?? '') !== (c ?? '');
  let next: TaskRequest = original;
  const phases = original.phases ?? [];

  if (form.category === 'station' || form.category === 'charge') {
    const key = form.category === 'station' ? 'destination' : 'chargePlace';
    if (changed(v[key], b[key])) {
      if (!v[key]) return unsupportedTask('it needs a place to go to');
      next = { ...next, phases: [withPlace(phases[0]!, v[key]!)] };
    }
  } else if (form.category === 'dustbag') {
    let phase = phases[0]!;
    if (changed(v.dustbagStation, b.dustbagStation)) {
      if (!v.dustbagStation) return unsupportedTask('it needs a maintenance point');
      phase = withPlace(phase, v.dustbagStation);
    }
    if (changed(v.dustbagWaitMinutes, b.dustbagWaitMinutes)) {
      const minutes = parseDustbagWaitMinutes(v.dustbagWaitMinutes);
      if (minutes === 'invalid') {
        return unsupportedTask(
          `the wait must be whole minutes from ${DUSTBAG_WAIT_MIN_MINUTES} to ${DUSTBAG_WAIT_MAX_MINUTES}, or empty`,
        );
      }
      // Emptied: the site's default applies again, so the field is
      // REMOVED rather than set to it (`buildPhases` says why).
      phase =
        minutes === null
          ? { ...phase, params: omitKey(phase.params, 'timeout_s') }
          : { ...phase, params: { ...phase.params, timeout_s: minutes * 60 } };
    }
    if (phase !== phases[0]) next = { ...next, phases: [phase] };
  } else if (form.category === 'patrol') {
    const stops = waypointList(v.waypoints);
    if (stops.join('\n') !== waypointList(b.waypoints).join('\n')) {
      if (stops.length === 0) return unsupportedTask('it needs at least one place to visit');
      // A different list of stops is a different route: built fresh,
      // as the create form builds one. Per-stop params cannot follow a
      // stop that moved, so none is guessed at.
      next = { ...next, phases: buildPhases('patrol', { waypoints: stops.join(',') }) };
    }
  } else {
    if (changed(v.zone, b.zone) || form.cleanMode !== base.cleanMode) {
      if (!v.zone) return unsupportedTask('it needs a zone to clean');
      const phase = phases[0]!;
      next = {
        ...next,
        phases: [
          {
            ...phase,
            description: `${CLEAN_MODE_LABEL[form.cleanMode]} ${v.zone}`,
            params: { ...phase.params, zone: v.zone, mode: form.cleanMode },
          },
        ],
      };
    }
  }

  if (changed(v.fleet, b.fleet)) {
    next = v.fleet ? { ...next, fleet: v.fleet } : omitKey(next, 'fleet');
    next = omitKey(next, 'robot');
  }
  if (priorityLevel(v.priority) !== priorityLevel(b.priority)) {
    // A NUMBER, the level chosen: the Edit drawer's setter for it is not
    // the string one, so "1" never stands in for 1. Untouched, the stored
    // value goes back as it was -- a supervisor's High stays High under
    // anyone's edit, and keeping High is not making it
    // (`task_priority.py`).
    next = { ...next, priority: priorityLevel(v.priority) };
  }
  if (changed(v.notes, b.notes)) {
    next = { ...next, description: v.notes?.trim() || toV1Category(form.category) };
  }
  return next;
}
